import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { FormInputProps } from '@/@types/form.types';
import { add, format } from 'date-fns';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import CalendarIcon from '@/components/icons/CalendarIcon';
import InformationCircle from '@/components/icons/InformationCircle';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

type Props<T extends {}> = {
	align?: 'start' | 'center' | 'end';
	fromDate?: Date;
} & FormInputProps<T>;

const EDatePicker = <T extends {}>({
	label,
	fieldName,
	control,
	disabled,
	placeholder = 'Select date',
	align,
	fromDate,
}: Props<T>) => {
	const [isOpen, setIsOpen] = useState(false);

	const { t } = useTranslation();
	
	return (
		<div className='w-full flex flex-col gap-1.5'>
			{label && <h4 className='text-sm font-medium'>{label}</h4>}
			<Controller
				name={fieldName}
				control={control}
				render={({
					field: { value, onChange },
					fieldState,
					formState,
				}) => {
					const handleSelect = (newDay: Date | undefined) => {
						if (!newDay) return null;

						let newDateFull = newDay;

						if (value) {
							const diff =
								newDay.getTime() - new Date(value).getTime();
							const diffInDays = diff / (1000 * 60 * 60 * 24);
							newDateFull = add(value, {
								days: Math.ceil(diffInDays),
							});
						}

						onChange(newDateFull);
						setIsOpen(false);
					};

					return (
						<Popover
							onOpenChange={setIsOpen}
							open={isOpen}
							modal={true}>
							<PopoverTrigger asChild>
								<Button
									variant={'outline'}
									className={cn(
										'px-3.5 rounded-semi-xl justify-between text-xs transition',
										fieldState.invalid
											? 'border-error-1000'
											: '',
										isOpen ? 'border-primary-color-1000' : '',
									)}>
									{value ? (
										format(value, 'd MMM, yyyy')
									) : (
										<span className='text-popover-800'>
											{placeholder}
										</span>
									)}
									<CalendarIcon
										className={'stroke-popover-1000'}
									/>
								</Button>
							</PopoverTrigger>
							<PopoverContent
								className='w-auto p-0'
								align={align}>
								<Calendar
									mode='single'
									initialFocus
									selected={value}
									onSelect={(d) => handleSelect(d)}
									disabled={disabled || formState.disabled}
									fromDate={fromDate}
								/>
							</PopoverContent>
							{fieldState.invalid && (
								<div className='flex items-center gap-1.5'>
									<InformationCircle className='size-4 shrink-0 stroke-error-1000' />
									<span className='text-xs text-error-1000'>
										{t(`${fieldState.error?.message}`)}
									</span>
								</div>
							)}
						</Popover>
					);
				}}
			/>
		</div>
	);
};

export default EDatePicker;
